import { Link } from '@tanstack/react-router'
import { CircleAlert, TriangleAlert, Truck } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { RowChip } from '../lib/row-stock'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * The row's one-line explanation, as a chip in the SKU line
 * (CHG-2026-09-menu-editor-redesign).
 *
 * A sentence there wrapped and broke the row apart, so the chip carries only the verdict and the
 * day; the sentence lives in the tooltip, and the chip itself is the way to the delivery note.
 * Deliveries have an expected DATE and no time, so the chip never shows one.
 */
export function DeliveryChip({
  chip,
  menuDate,
  restaurantId,
}: {
  chip: RowChip
  menuDate?: string
  restaurantId: number
}) {
  const { t } = useTranslation('menus')
  if (!chip) return null

  const date = formatDay(menuDate)

  const tone = {
    delivery: 'bg-[oklch(0.955_0.025_155)] text-[oklch(0.42_0.10_155)]',
    notCounting: 'bg-[oklch(0.965_0.035_80)] text-[#8a5e10]',
    soldOut: 'bg-[oklch(0.955_0.030_27)] text-[oklch(0.50_0.19_27)]',
  }[chip.kind]

  const Icon =
    chip.kind === 'delivery'
      ? Truck
      : chip.kind === 'notCounting'
        ? TriangleAlert
        : CircleAlert

  const deliveries = chip.kind === 'soldOut' ? [] : chip.deliveries
  const supplier =
    deliveries.length === 1
      ? deliveries[0].supplier
      : t('itemRow.chip.several', { count: deliveries.length })

  const label =
    chip.kind === 'soldOut'
      ? t('itemRow.chip.soldOut')
      : t(`itemRow.chip.${chip.kind}`, { date })

  const hint =
    chip.kind === 'soldOut'
      ? t('itemRow.chip.soldOutHint', { date })
      : t(`itemRow.chip.${chip.kind}Hint`, { supplier, date })

  const body = (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-px text-[11px] font-semibold whitespace-nowrap',
        tone,
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Exactly one document: go straight to it. Several, or none, is not a single target. */}
        {deliveries.length === 1 ? (
          <Link
            to="/inventory/deliveries/$id"
            params={{ id: deliveries[0].id }}
            // The delivery endpoints are restaurant-scoped and the page's persisted selection may
            // point at another store, so the deep link carries the menu's restaurant.
            search={{ restaurantId }}
            aria-label={hint}
          >
            {body}
          </Link>
        ) : (
          <span aria-label={hint} className="cursor-help">
            {body}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{hint}</TooltipContent>
    </Tooltip>
  )
}

/** The menu's ISO day as DD.MM. — the chip has no room for a year and no time to show. */
function formatDay(iso?: string): string {
  if (!iso) return ''
  const [, month, day] = iso.split('-')
  return month && day ? `${day}.${month}.` : iso
}
